import React, { useMemo } from 'react';
import { Eye, Pencil } from 'lucide-react';
import { APPLICATION_HISTORY_COLUMNS } from '../../../constants/fundingApplicationOptions';
import { resolveOrderedVisibleColumns } from './applicationHistoryColumnConfig';
import {
  formatCurrency,
  formatDate,
  formatTimeDuration,
} from '../../../utils/fundingWorkspaceFormatters';
import CopyableValue from './CopyableValue';
import SecureEinCell from './SecureEinCell';
import SecureSsnField from './SecureSsnField';
import {
  formatApplicantPills,
  formatApplicationSource,
  formatBureauPullLabel,
  resolveApplicationEntityLabel,
} from './fundingApplicationDisplay';

const getCellValue = (app, columnId, client) => {
  switch (columnId) {
    case 'dateOfApplication':
      return { raw: app.dateOfApplication, display: formatDate(app.dateOfApplication) };
    case 'entity':
      return { raw: app.entityId, display: resolveApplicationEntityLabel(app, client), pill: 'entity' };
    case 'applicants':
      return { raw: app.applicants, display: null, pill: 'applicants' };
    case 'lender':
      return { raw: app.lender, display: app.lender };
    case 'lendingProduct':
      return { raw: app.lendingProduct, display: app.lendingProduct };
    case 'applicationType':
      return { raw: app.applicationType, display: app.applicationType };
    case 'personalOrBusiness':
      return { raw: app.personalOrBusiness, display: app.personalOrBusiness };
    case 'zeroPercentOffer':
      return { raw: app.zeroPercentOffer, display: app.zeroPercentOffer };
    case 'approvalAmount':
      return { raw: app.approvalAmount, display: formatCurrency(app.approvalAmount) };
    case 'decision':
      return { raw: app.decision, display: app.decision };
    case 'zeroPercentTerm':
      return { raw: app.zeroPercentTerm, display: app.zeroPercentTerm };
    case 'bureauPull':
      return { raw: app.selectedBureauPull, display: formatBureauPullLabel(app.selectedBureauPull) };
    case 'source':
      return { raw: app.source, display: formatApplicationSource(app.source) };
    case 'ownerName':
      return { raw: app.ownerName, display: app.ownerName };
    case 'ssn':
      return { sensitive: true };
    case 'dob':
      return { raw: app.dob, display: formatDate(app.dob) };
    case 'fullEin':
      return { raw: app.einMasked || app.fullEin, display: app.einMasked || app.fullEin, ein: true };
    case 'businessStartDate':
      return { raw: app.businessStartDate, display: formatDate(app.businessStartDate) };
    case 'employerName':
      return { raw: app.employerName, display: app.employerName };
    case 'timeLiveInHouse':
      return {
        raw: formatTimeDuration(app.timeLiveInHouseYears, app.timeLiveInHouseMonths),
        display: formatTimeDuration(app.timeLiveInHouseYears, app.timeLiveInHouseMonths),
      };
    case 'timeWorkingAt':
      return {
        raw: formatTimeDuration(app.timeWorkingAtYears, app.timeWorkingAtMonths),
        display: formatTimeDuration(app.timeWorkingAtYears, app.timeWorkingAtMonths),
      };
    case 'experianScoreUsed':
      return { raw: app.experianScoreUsed, display: app.experianScoreUsed };
    case 'transunionScoreUsed':
      return { raw: app.transunionScoreUsed, display: app.transunionScoreUsed };
    case 'equifaxScoreUsed':
      return { raw: app.equifaxScoreUsed, display: app.equifaxScoreUsed };
    case 'personalIncome':
      return { raw: app.personalIncome, display: formatCurrency(app.personalIncome) };
    case 'businessIncome':
      return { raw: app.businessIncome, display: formatCurrency(app.businessIncome) };
    case 'housing':
      return { raw: app.housing, display: formatCurrency(app.housing) };
    case 'businessExpenses':
      return { raw: app.businessExpenses, display: formatCurrency(app.businessExpenses) };
    case 'savings':
      return { raw: app.savings, display: formatCurrency(app.savings) };
    case 'retirementAccount':
      return { raw: app.retirementAccount, display: formatCurrency(app.retirementAccount) };
    case 'personalAddress':
      return { raw: app.personalAddress, display: app.personalAddress };
    case 'businessAddress':
      return { raw: app.businessAddress, display: app.businessAddress };
    case 'notes':
      return { raw: app.notes, display: app.notes };
    default:
      return { raw: null, display: '—' };
  }
};

const FundingApplicationHistoryTable = ({
  applications = [],
  columnConfig = null,
  visibleColumns = [],
  clientId,
  agencyId,
  client,
  entityFilter = 'all',
  onEntityFilterChange,
  onOpenRecord,
}) => {
  const columns = useMemo(() => {
    if (columnConfig?.length) {
      return resolveOrderedVisibleColumns(columnConfig);
    }
    const byId = new Map(APPLICATION_HISTORY_COLUMNS.map((col) => [col.id, col]));
    return visibleColumns
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [columnConfig, visibleColumns]);

  const filteredApplications = useMemo(() => {
    if (entityFilter !== 'current' || !client?.selectedEntityId) return applications;
    return applications.filter((app) => app.entityId === client.selectedEntityId);
  }, [applications, entityFilter, client?.selectedEntityId]);

  return (
    <div className="space-y-3">
      <div className="fg-history-filter-row">
        <div className="fg-history-filter-buttons" data-testid="application-history-entity-filter">
          <button
            type="button"
            className={`fg-history-filter-btn ${entityFilter === 'all' ? 'active' : ''}`}
            data-testid="history-filter-all-entities"
            onClick={() => onEntityFilterChange?.('all')}
          >
            All entities
          </button>
          <button
            type="button"
            className={`fg-history-filter-btn ${entityFilter === 'current' ? 'active' : ''}`}
            data-testid="history-filter-current-entity"
            onClick={() => onEntityFilterChange?.('current')}
          >
            Current entity
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-[var(--fg-line)]">
        <table className="min-w-full text-left text-sm" data-testid="funding-application-history-table">
          <thead>
            <tr className="border-b border-[var(--fg-line)] bg-[var(--fg-panel-soft)] text-[var(--fg-muted)]">
              {columns.map((col) => (
                <th key={col.id} className="whitespace-nowrap px-3 py-2 font-medium">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredApplications.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-[var(--fg-muted)]">
                  No applications yet. Click Add Application to create one.
                </td>
              </tr>
            ) : (
              filteredApplications.map((app) => (
                <tr
                  key={app.id}
                  className="cursor-pointer border-b border-[var(--fg-line)]/70 hover:bg-[var(--fg-accent-soft)]/30"
                  onClick={() => onOpenRecord?.(app)}
                >
                  {columns.map((col) => {
                    if (col.id === 'actions') {
                      return (
                        <td key={col.id} className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => onOpenRecord?.(app)}
                              className="rounded-lg p-1.5 text-[var(--fg-muted)] hover:bg-white hover:text-[var(--fg-accent)]"
                              aria-label="View application"
                            >
                              <Eye className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => onOpenRecord?.(app, true)}
                              className="rounded-lg p-1.5 text-[var(--fg-muted)] hover:bg-white hover:text-[var(--fg-accent)]"
                              aria-label="Edit application"
                            >
                              <Pencil className="size-4" />
                            </button>
                          </div>
                        </td>
                      );
                    }
                    const cell = getCellValue(app, col.id, client);
                    return (
                      <td key={col.id} className="whitespace-nowrap px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        {col.id === 'ssn' ? (
                          <SecureSsnField
                            maskedValue={app.ssnMasked}
                            ssnLast4={app.ssnLast4}
                            clientId={clientId}
                            agencyId={agencyId}
                            applicationId={app.id}
                          />
                        ) : col.id === 'fullEin' ? (
                          <SecureEinCell value={cell.raw} />
                        ) : col.id === 'entity' ? (
                          <span className="fg-entity-pill">{cell.display || '—'}</span>
                        ) : col.id === 'applicants' ? (
                          <div className="fg-applicant-pills">{formatApplicantPills(app, client)}</div>
                        ) : col.id === 'source' ? (
                          <span className={`fg-source-pill ${app.source === 'uploaded_scan' ? 'upload' : 'manual'}`}>
                            {cell.display}
                          </span>
                        ) : (
                          <CopyableValue value={cell.raw} displayValue={cell.display} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FundingApplicationHistoryTable;
